import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import type { TotpSetup } from '../auth/types';
import { DEMO_MODE } from '../config';
import { errorMessage } from '../components/States';
import { TotpEnrolment } from '../components/TotpEnrolment';

type Mode = 'signin' | 'new-password' | 'totp' | 'totp-setup' | 'forgot' | 'forgot-confirm';

export function LoginPage({ mspName }: { mspName: string }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [totpSetup, setTotpSetup] = useState<TotpSetup | null>(null);

  if (auth.status === 'signed-in') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleOutcome = (outcome: Awaited<ReturnType<typeof auth.signIn>>) => {
    if (outcome.kind === 'done') navigate('/', { replace: true });
    else if (outcome.kind === 'new-password') setMode('new-password');
    else if (outcome.kind === 'totp') setMode('totp');
    else if (outcome.kind === 'totp-setup') {
      setTotpSetup(outcome.setup);
      setMode('totp-setup');
    } else setError(`Unsupported sign-in step: ${outcome.step}`);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'signin') return run(async () => handleOutcome(await auth.signIn(email.trim(), password)));
    if (mode === 'new-password') {
      if (password !== password2) return setError('Passwords do not match');
      return run(async () => handleOutcome(await auth.confirmNewPassword(password)));
    }
    if (mode === 'totp') return run(async () => handleOutcome(await auth.confirmTotp(code.trim())));
    if (mode === 'forgot') {
      return run(async () => {
        await auth.requestPasswordReset(email.trim());
        setNotice('If that account exists, a reset code has been emailed to it.');
        setMode('forgot-confirm');
      });
    }
    if (mode === 'forgot-confirm') {
      if (password !== password2) return setError('Passwords do not match');
      return run(async () => {
        await auth.confirmPasswordReset(email.trim(), code.trim(), password);
        setNotice('Password updated. Sign in with your new password.');
        setPassword('');
        setPassword2('');
        setMode('signin');
      });
    }
  };

  if (mode === 'totp-setup' && totpSetup) {
    return (
      <div className="login">
        <div className="login__card form">
          <div className="login__brand">
            <span className="topbar__logo" aria-hidden="true" />
            <div>
              <h1>Set up two-step verification</h1>
              <div className="small muted">{mspName} requires an authenticator app for every account.</div>
            </div>
          </div>
          <TotpEnrolment
            setup={totpSetup}
            submitLabel="Verify and sign in"
            onVerify={async (code) => handleOutcome(await auth.confirmTotp(code))}
            onCancel={() => { setMode('signin'); setTotpSetup(null); setError(null); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <form className="login__card form" onSubmit={submit}>
        <div className="login__brand">
          <span className="topbar__logo" aria-hidden="true" />
          <div>
            <h1>{mspName}</h1>
            <div className="small muted">IoT dashboard</div>
          </div>
        </div>

        {error ? <div className="form-error">{error}</div> : null}
        {notice ? <div className="form-success">{notice}</div> : null}

        {(mode === 'signin' || mode === 'forgot') && (
          <label>
            <div className="small secondary">Email</div>
            <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        )}
        {mode === 'signin' && (
          <label>
            <div className="small secondary">Password</div>
            <input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
        )}
        {(mode === 'new-password' || mode === 'forgot-confirm') && (
          <>
            {mode === 'new-password' ? <p className="secondary">Choose a new password to finish setting up your account.</p> : null}
            {mode === 'forgot-confirm' ? (
              <label>
                <div className="small secondary">Reset code</div>
                <input className="input" required value={code} onChange={(e) => setCode(e.target.value)} />
              </label>
            ) : null}
            <label>
              <div className="small secondary">New password</div>
              <input className="input" type="password" autoComplete="new-password" required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} />
              <div className="form-row__hint">At least 12 characters with upper and lower case letters and a digit.</div>
            </label>
            <label>
              <div className="small secondary">Confirm new password</div>
              <input className="input" type="password" autoComplete="new-password" required value={password2} onChange={(e) => setPassword2(e.target.value)} />
            </label>
          </>
        )}
        {mode === 'totp' && (
          <label>
            <div className="small secondary">Authenticator code</div>
            <input className="input" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
        )}

        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : mode === 'forgot' ? 'Send reset code' : 'Continue'}
        </button>

        <div className="row" style={{ justifyContent: 'space-between' }}>
          {mode === 'signin' ? (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setMode('forgot'); setError(null); }}>
              Forgot password?
            </button>
          ) : (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setMode('signin'); setError(null); }}>
              Back to sign in
            </button>
          )}
        </div>

        {DEMO_MODE ? (
          <div className="login__hint">
            <strong>Demo mode.</strong> Any password works. Sign in as <code>ops@msp.example</code> for the MSP portal, <code>tenant@acme.example</code> for a client administrator, or <code>viewer@acme.example</code> for a read-only client user.
          </div>
        ) : (
          <div className="login__hint">Access is by invitation only. Contact your service provider if you need an account.</div>
        )}
      </form>
    </div>
  );
}
